/**
 * Authentication context provider.
 * Manages user authentication state and operations.
 *
 * This is a thin orchestrator that composes extracted hooks:
 * - useTokenManagement: Token verification, refresh, and initialization
 * - useAuthOperations: Login, register, and logout
 * - useProfileOperations: Profile and language settings updates
 */

import type { ReactNode } from 'react'
import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import * as Sentry from '@sentry/react'
import { clearRefreshState } from '../services/auth-interceptor'
import type { TokenManager } from '../services/token-manager'
import { tokenManager as defaultTokenManager } from '../services/token-manager'
import { type User, type AuthStatus, type LanguageSettings, AuthError } from './auth'
import type { AvatarConfig } from '@/types/avatar'
import { useTokenManagement } from './auth/useTokenManagement'
import { useAuthOperations } from './auth/useAuthOperations'
import { useProfileOperations } from './auth/useProfileOperations'
import { NotificationContext } from './NotificationContext'
import { crossTabCoordinator, type TokenClearReason } from '../services/cross-tab-coordinator'
import { trackTokenCleared } from '../services/analytics.service'
import { systemClock } from '../lib/clock'
import { getBrowserAdapter } from '../lib/browserAdapter'
import { browserScheduler } from '../lib/scheduler'
import { useServiceContext } from './ServiceContext'

// Re-export types for backward compatibility
export type { User, AuthStatus, LanguageSettings }
export { AuthError }

export interface AuthContextType {
    user: User | null
    login: (username: string, password: string) => Promise<void>
    register: (
        username: string,
        email: string,
        password: string,
        userLang: string,
        targetLang: string,
        inviteKey?: string
    ) => Promise<void>
    updateProfile: (updates: {
        display_name?: string | null
        avatar_url?: string | null
        avatar_config?: AvatarConfig | null
    }) => Promise<User>
    patchProfile: (updates: {
        display_name?: string | null
        avatar_url?: string | null
        avatar_config?: AvatarConfig | null
    }) => Promise<User>
    updateLanguageSettings: (settings: LanguageSettings) => Promise<User>
    logout: () => void
    handleAuthFailure: () => void
    resetIntentionalLogout: () => void
    loading: boolean
    hydrated: boolean
    status: AuthStatus
    isAuthenticated: boolean
    error: string | null
    isIntentionalLogout: boolean
    lastError: {
        message: string
        timestamp: number
        tabId: string
        errorType?: string
        status?: number
    } | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export { AuthContext }

// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

/**
 * Selector hook that returns only the user object.
 * Components using this hook will only re-render when the user changes.
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useAuthUser = (): User | null => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuthUser must be used within an AuthProvider')
    }
    // Memoize to prevent re-renders when other context fields change
    return useMemo(() => context.user, [context.user])
}

/**
 * Selector hook that returns only auth status fields (loading, status, isAuthenticated).
 * Components using this hook will only re-render when these fields change.
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useAuthStatus = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuthStatus must be used within an AuthProvider')
    }
    // Memoize to prevent re-renders when user or actions change
    return useMemo(
        () => ({
            loading: context.loading,
            hydrated: context.hydrated,
            status: context.status,
            isAuthenticated: context.isAuthenticated,
            error: context.error,
            isIntentionalLogout: context.isIntentionalLogout,
        }),
        [
            context.loading,
            context.hydrated,
            context.status,
            context.isAuthenticated,
            context.error,
            context.isIntentionalLogout,
        ]
    )
}

/**
 * Selector hook that returns only auth action functions.
 * Components using this hook will only re-render when action functions change (rare).
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useAuthActions = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuthActions must be used within an AuthProvider')
    }
    // Memoize to prevent re-renders when user or status change
    return useMemo(
        () => ({
            login: context.login,
            register: context.register,
            updateProfile: context.updateProfile,
            patchProfile: context.patchProfile,
            updateLanguageSettings: context.updateLanguageSettings,
            logout: context.logout,
            handleAuthFailure: context.handleAuthFailure,
            resetIntentionalLogout: context.resetIntentionalLogout,
        }),
        [
            context.login,
            context.register,
            context.updateProfile,
            context.patchProfile,
            context.updateLanguageSettings,
            context.logout,
            context.handleAuthFailure,
            context.resetIntentionalLogout,
        ]
    )
}

interface AuthProviderProps {
    children: ReactNode
    /** Token manager for storage operations (defaults to browserStorage-backed tokenManager) */
    tokenManager?: TokenManager
    /** Initial user state for testing (bypasses initialization) */
    initialUser?: User | null
    /** Initial auth status for testing (bypasses initialization) */
    initialStatus?: AuthStatus
    /** Skip automatic initialization from storage (useful for testing) */
    skipInitialization?: boolean
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
    children,
    tokenManager = defaultTokenManager,
    initialUser,
    initialStatus,
    skipInitialization = false,
}) => {
    // Get scheduler from ServiceContext for testability, fallback to browserScheduler
    const serviceContext = useServiceContext()
    const scheduler = serviceContext?.scheduler ?? browserScheduler

    // Core state
    const [user, setUser] = useState<User | null>(initialUser ?? null)
    const [loading, setLoading] = useState(false)
    const [hydrated, setHydrated] = useState(skipInitialization)
    const [status, setStatus] = useState<AuthStatus>(initialStatus ?? 'signedOut')
    const [error, setError] = useState<string | null>(null)
    const [isIntentionalLogout, setIsIntentionalLogout] = useState(false)
    const [lastError, setLastError] = useState<AuthContextType['lastError']>(null)

    // Sync Sentry user context with auth state (UUID only, never email)
    useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.id })
        } else {
            Sentry.setUser(null)
        }
    }, [user])

    // Track if we're currently clearing tokens to avoid loops
    const isClearingTokensRef = useRef(false)
    // Track if we're currently initializing to prevent duplicate calls
    const isInitializingRef = useRef(false)

    // Clear all auth data
    // Note: Does not reset isIntentionalLogout - that must be done explicitly
    const clearAuthData = useCallback(
        (
            reason: TokenClearReason = 'unknown',
            errorDetails?: { errorType?: string; status?: number; message?: string }
        ) => {
            // Prevent recursive clearing
            if (isClearingTokensRef.current) {
                return
            }
            isClearingTokensRef.current = true

            const tabId = crossTabCoordinator.getTabId()

            // Store error details for observability
            if (errorDetails) {
                setLastError({
                    message: errorDetails.message || 'Authentication error',
                    timestamp: systemClock.now(),
                    tabId,
                    errorType: errorDetails.errorType,
                    status: errorDetails.status,
                })
            }

            // Emit telemetry
            trackTokenCleared(reason, tabId, {
                error_type: errorDetails?.errorType,
                status: errorDetails?.status,
                message: errorDetails?.message,
            })

            // Broadcast to other tabs
            crossTabCoordinator.broadcastTokenCleared(reason, errorDetails)

            setUser(null)
            setStatus('signedOut')
            setError(null)
            setLoading(false)
            tokenManager.clearTokens()
            clearRefreshState()

            // Reset clearing flag after a short delay
            scheduler.setTimeout(() => {
                isClearingTokensRef.current = false
            }, 100)
        },
        [tokenManager, scheduler]
    )

    // Handle auth failure (e.g., refresh token expired)
    const handleAuthFailure = useCallback(() => {
        // Reset intentional logout flag for session expiry cases
        setIsIntentionalLogout(false)
        clearAuthData('session_expired', {
            errorType: 'AuthError',
            message: 'Session expired',
        })
    }, [clearAuthData])

    // Reset intentional logout flag (called after LoginPage reads loggedOut state)
    const resetIntentionalLogout = useCallback(() => {
        setIsIntentionalLogout(false)
    }, [])

    // Get notification context (optional - may not be available in all contexts)
    const notificationContext = useContext(NotificationContext)

    // Create notification callback for useTokenManagement
    const showAuthNotification = useCallback(
        (translationKey: string, severity?: 'success' | 'error' | 'warning' | 'info') => {
            if (notificationContext) {
                notificationContext.showNotification(translationKey, { severity })
            }
        },
        [notificationContext]
    )

    // Token management operations
    const { initializeAuth } = useTokenManagement({
        tokenManager,
        setUser,
        setStatus,
        setLoading,
        setHydrated,
        clearAuthData,
        showNotification: showAuthNotification,
    })

    // Authentication operations
    const { login, register, logout } = useAuthOperations({
        tokenManager,
        setUser,
        setStatus,
        setLoading,
        setError,
        clearAuthData,
        setIsIntentionalLogout,
    })

    // Profile operations
    const { updateProfile, patchProfile, updateLanguageSettings } = useProfileOperations({
        user,
        tokenManager,
        setUser,
        setLoading,
        setError,
    })

    // Initialize cross-tab coordinator
    useEffect(() => {
        crossTabCoordinator.initialize()
        return () => {
            crossTabCoordinator.destroy()
        }
    }, [])

    // Listen for cross-tab token clearing events
    useEffect(() => {
        const handleTokensCleared = (event: CustomEvent) => {
            const clearEvent = event.detail as {
                reason: TokenClearReason
                timestamp: number
                tabId: string
                errorDetails?: { errorType?: string; status?: number; message?: string }
            }

            // Only handle if it's from another tab
            if (clearEvent.tabId !== crossTabCoordinator.getTabId()) {
                // Another tab cleared tokens
                // Only clear in this tab if it was intentional (logout) or confirmed auth error
                if (clearEvent.reason === 'logout' || clearEvent.reason === 'auth_error') {
                    // Store error details for observability
                    if (clearEvent.errorDetails) {
                        setLastError({
                            message: clearEvent.errorDetails.message || 'Authentication error',
                            timestamp: clearEvent.timestamp,
                            tabId: clearEvent.tabId,
                            errorType: clearEvent.errorDetails.errorType,
                            status: clearEvent.errorDetails.status,
                        })
                    }

                    // Clear tokens in this tab too (but don't broadcast again)
                    if (!isClearingTokensRef.current) {
                        isClearingTokensRef.current = true
                        setUser(null)
                        setStatus('signedOut')
                        setError(null)
                        setLoading(false)
                        tokenManager.clearTokens()
                        clearRefreshState()
                        scheduler.setTimeout(() => {
                            isClearingTokensRef.current = false
                        }, 100)
                    }
                }
                // For 'session_expired' or 'unknown', we don't automatically clear
                // to prevent one tab's transient error from affecting others
            }
        }

        const browserAdapter = getBrowserAdapter()
        browserAdapter.addEventListener('auth-tokens-cleared', handleTokensCleared as EventListener)
        return () => {
            browserAdapter.removeEventListener(
                'auth-tokens-cleared',
                handleTokensCleared as EventListener
            )
        }
    }, [tokenManager, scheduler])

    // Initialize auth state on mount
    useEffect(() => {
        if (!skipInitialization && !isInitializingRef.current) {
            isInitializingRef.current = true
            // Capture current location for route preservation
            const browserAdapter = getBrowserAdapter()
            const browserLocation = browserAdapter.getLocation()
            // Parse href to extract search and hash
            let search = ''
            let hash = ''
            try {
                const url = new URL(browserLocation.href)
                search = url.search
                hash = url.hash
            } catch {
                // Fallback: parse manually if URL constructor fails
                const hashIndex = browserLocation.href.indexOf('#')
                const searchIndex = browserLocation.href.indexOf('?')
                if (hashIndex !== -1) {
                    hash = browserLocation.href.substring(hashIndex)
                }
                if (searchIndex !== -1) {
                    const searchEnd = hashIndex !== -1 ? hashIndex : browserLocation.href.length
                    search = browserLocation.href.substring(searchIndex, searchEnd)
                }
            }
            const location = {
                pathname: browserLocation.pathname,
                search,
                hash,
            }
            void initializeAuth(location).finally(() => {
                isInitializingRef.current = false
            })
        }
    }, [initializeAuth, skipInitialization])

    // Memoized context value
    const value = useMemo<AuthContextType>(
        () => ({
            user,
            login,
            register,
            updateProfile,
            patchProfile,
            updateLanguageSettings,
            logout,
            handleAuthFailure,
            resetIntentionalLogout,
            loading,
            hydrated,
            status,
            isAuthenticated: status === 'signedIn',
            error,
            isIntentionalLogout,
            lastError,
        }),
        [
            user,
            login,
            register,
            updateProfile,
            patchProfile,
            updateLanguageSettings,
            logout,
            handleAuthFailure,
            resetIntentionalLogout,
            loading,
            hydrated,
            status,
            error,
            isIntentionalLogout,
            lastError,
        ]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
